import { Injectable, inject } from '@angular/core';
import { from, type Observable } from 'rxjs';
import { SiteFacade } from '@web/store/website/site/site.facade';
import type { CatalogProductCard } from '@web/widgets/product-card/type/product-card-widget.type';
import type { ProductCard } from '@common/types/product-card.type';
import { type SelectedFileInterface } from '@sendpulse/file-manager';
import {
    extendWebFileDataPromise,
    preparedSelectedFile,
    mapCatalogProductToCard,
    prepareImages,
} from '@common/helpers';
import { type SelectedFileExtended } from '@common/types/file.type';
import { switchMap } from 'rxjs/operators';
import { UserFacade } from '@web/store/user/user.facade';

@Injectable({
    providedIn: 'root',
})
export class ProductCardService {
    private readonly websiteFacade = inject(SiteFacade);
    private readonly userFacade = inject(UserFacade);

    public transformProductData(catalogProduct: CatalogProductCard): Observable<ProductCard> {
        return this.userFacade.userCdnInfo$.pipe(
            switchMap(
                ({ userHash, cdn }) =>
                    from(
                        (async (): Promise<ProductCard> => {
                            const transformedImages: SelectedFileInterface[] = prepareImages(
                                catalogProduct.images,
                                userHash,
                                cdn
                            );

                            const extendedImages: SelectedFileExtended[] = [];

                            for (const file of transformedImages) {
                                try {
                                    const extendedFile = await extendWebFileDataPromise(file, `https://${cdn}`);

                                    extendedImages.push(extendedFile);

                                    // catalogProduct.images may be full URLs; resize expects an absolute storage path
                                    const path = /^https?:\/\//.test(extendedFile.path)
                                        ? extendedFile.path.replace(/^https?:\/\/[^/]+/, '')
                                        : extendedFile.path;

                                    this.websiteFacade.resizeImage({
                                        path,
                                        url: `https://${extendedFile.defaultCdnUrl}${extendedFile.url}`,
                                    });
                                } catch (e) {
                                    // console.error('Error extending file data', e);
                                }
                            }

                            return {
                                ...mapCatalogProductToCard(catalogProduct, extendedImages),
                                userHash,
                            };
                        })()
                    ) as Observable<ProductCard>
            )
        );
    }
}
